import { Injectable } from '@angular/core';
import { ResourceEffect } from './resource.effect';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { State } from '@completion/reducers';
import { NotificationService } from '../../services/notification.service';
import { BulkService } from '../../services/bulk.service';
import {
  BulkActionTypes,
  LoadAvailableCheckSheetAssignments,
  LoadAvailableCheckSheetAssignmentsSuccess,
  LoadAvailableCheckSheets,
  LoadAvailableCheckSheetsSuccess,
  LoadAvailableTargetCheckSheetAssignments,
  LoadAvailableTargetCheckSheetAssignmentsSuccess,
} from '@completion/actions';
import { withLatestFrom } from 'rxjs/operators';
import { getCurrentProject, getSourceCheckSheetAssignment, getTargetCheckSheetAssignments } from '@completion/selectors';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class BulkEffect extends ResourceEffect {
  constructor(
    readonly actions$: Actions,
    readonly bulkService: BulkService,
    readonly store: Store<State>,
    readonly toastr: NotificationService,
  ) {
    super(actions$, bulkService, store, toastr);
  }

  loadAvailableCheckSheets$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadAvailableCheckSheets>(BulkActionTypes.LoadAvailableCheckSheets),
      withLatestFrom(this.store.select((state) => state.project.currentProject)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.bulkService,
        method: 'getAvailableCheckSheets',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (data) => new LoadAvailableCheckSheetsSuccess(data as any),
      }),
    ),
  );

  loadAvailableCheckSheetAssignments$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadAvailableCheckSheetAssignments>(BulkActionTypes.LoadAvailableCheckSheetAssignments),
      withLatestFrom(this.store.select((state) => state.project.currentProject)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.bulkService,
        method: 'getAvailableSourceCheckSheetAssignments',
        serviceArgs: ([action, currentProject]) => [action.checkSheetId, currentProject.id],
        onSuccess: (data) => new LoadAvailableCheckSheetAssignmentsSuccess(data as any),
      }),
    ),
  );

  loadAvailableTargetCheckSheetAssignments$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadAvailableTargetCheckSheetAssignments>(BulkActionTypes.LoadAvailableTargetCheckSheetAssignments),
      withLatestFrom(this.store.select((state) => state.project.currentProject)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.bulkService,
        method: 'getAvailableTargetCheckSheetAssignments',
        serviceArgs: ([action, currentProject]) => [action.checkSheetAssignmentId, currentProject.id],
        onSuccess: (data) => new LoadAvailableTargetCheckSheetAssignmentsSuccess(data as any),
      }),
    ),
  );

  copySourceToTarget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BulkActionTypes.CopySourceToTargets),
      withLatestFrom(
        this.store.select(getCurrentProject),
        this.store.select(getSourceCheckSheetAssignment),
        this.store.select(getTargetCheckSheetAssignments),
      ),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.bulkService,
        method: 'copySourceToTarget',
        serviceArgs: ([_, currentProject, source, targets]) => [currentProject.id, source.id, (targets || []).map((t) => t.id)],
        onSuccess: () => ({ type: BulkActionTypes.CopySourceToTargetsSuccess }),
      }),
    ),
  );
}
